import { applyCapacity, capacityBoxes } from "./capacity";

const ADDITIONAL_NAME = "additional_languages";

/**
 * Alpine scope for the shared LanguagesFieldset (wizard step 1 + website
 * management → Languages).
 *
 * Owns two concerns that have to agree with each other:
 *
 *  1. `primary` — the fieldset hides the additional-language row matching the
 *     primary language. Hiding alone left a checked-but-invisible box behind
 *     when the operator promoted an already-selected language to primary, which
 *     the server then counted twice against the plan's language cap. Promoting
 *     now unchecks it.
 *
 *  2. The plan's language cap. `limit` is the ADDITIONAL budget (the plan's
 *     total minus the primary), so the counter the operator sees matches the
 *     boxes they are ticking. -1 means unlimited.
 *
 * Exposes `used` / `limit` / `full` — the contract CapacitySelectionHint binds
 * its live counter and at-cap notice to.
 */
export function languagePicker(primary: string, limit: number) {
  return {
    primary,
    limit,
    used: 0,
    full: false,

    init() {
      this._sync();
      const root = (this as any).$el as HTMLElement;
      root.addEventListener("change", (e: Event) => {
        const t = e.target as HTMLInputElement;
        // The primary <select> carries x-model, so Alpine has already updated
        // this.primary by the time the event reaches us.
        if (t && t.name === "primary_language") this._dropPrimaryFromAdditional();
        this._sync();
      });
      (this as any).$watch("primary", () => {
        this._dropPrimaryFromAdditional();
        this._sync();
      });
    },

    _boxes(): HTMLInputElement[] {
      return capacityBoxes((this as any).$el as HTMLElement, ADDITIONAL_NAME);
    },

    // Clears the additional-language box for whatever is now the primary. The
    // row is hidden by x-show, so leaving it checked would silently spend a
    // language slot the operator cannot see or reclaim.
    _dropPrimaryFromAdditional() {
      for (const b of this._boxes()) {
        if (b.value === this.primary && b.checked) b.checked = false;
      }
    },

    _sync() {
      const state = applyCapacity(this._boxes(), this.limit);
      this.used = state.used;
      this.full = state.full;
    },
  };
}
